// src/redux/slices/bet/betSlice.js
import { createSlice } from "@reduxjs/toolkit";

const betSlice = createSlice({
  name: "bet",
  initialState: {
    selectedHeroRange: {},
    selectedAction: null, // 'bet', 'call', 'check', 'fold'
    lastApiResponse: null,
    bets: [], // { street, actor: 'hero'|'villain', action: 'bet'|'check'|'call'|'fold', betSizeBB?: number }
    lastBetSizeBB: null,
    // Villain range management
    villainCallRanges: {}, // {position: {combo: percentage}} - all possible villain calling ranges
    villainFutureCallRange: {}, // Pre-calculated villain call range for current situation
    selectedVillainRange: {}, // Currently selected villain range
    selectedVillainPosition: null, // Currently selected villain position
    // Pot and investment tracking
    potSize: 1.5, // Start with blinds
    investedDic: { lo: 0, hi: 0, co: 0, btn: 0, sb: 0.5, bb: 1.0 }, // Investment per position (current street only)
    investedDicAllStreets: { lo: 0, hi: 0, co: 0, btn: 0, sb: 0.5, bb: 1.0 }, // Cumulative investment across all streets
    stackSizesDic: { lo: 100, hi: 100, co: 100, btn: 100, sb: 99.5, bb: 99 }, // Stack sizes per position (SB/BB start with blinds posted)
    heroInvested: 0.0, // Hero's total investment
    investedTenPercentPre: false, // True if hero invested more than 9BB preflop
    betRatio: null, // Bet ratio as decimal (0.5 = 50% pot, 1.0 = 100% pot)
    currentStreet: 'preflop', // Track current street for investment management
    // Frozen pot sizes captured at the start of each postflop street
    frozenPotSize: { flop: null, turn: null, river: null },
    heroAction: null, // Last hero action
    villainAction: null, // Last villain action
    lastAggressor: null, // 'hero' | 'villain'
    lastAction: null, // Last action taken (hero or villain)
    lastTwoActions: [], // Last two actions taken
    betting: [], // List of action strings ['bet', 'call', 'check', 'fold']
    potType: null, // 'limp' (0 bets), 'open' (1 bet), '3bet' (2 bets), '4bet' (3+ bets)
    // Settings
    randomizerEnabled: true,
  },
  reducers: {
    setBetOption: (state, { payload }) => {
      state.selectedHeroRange = payload?.selectedBetOption?.value || {};
      state.selectedAction = 'bet';
      console.log('Selected Hero Range (BET):', state.selectedHeroRange);
    },
    setCallOption: (state, { payload }) => {
      state.selectedHeroRange = payload?.selectedCallOption?.value || {};
      state.selectedAction = 'call';
      console.log('Selected Hero Range (CALL):', state.selectedHeroRange);
    },
    setCheckOption: (state, { payload }) => {
      state.selectedHeroRange = payload?.selectedCheckOption?.value || {};
      state.selectedAction = 'check';
      console.log('Selected Hero Range (CHECK):', state.selectedHeroRange);
    },
    setFoldOption: (state, { payload }) => {
      state.selectedHeroRange = payload?.selectedFoldOption?.value || {};
      state.selectedAction = 'fold';
      console.log('Selected Hero Range (FOLD):', state.selectedHeroRange);
    },
    setApiResponse: (state, { payload }) => {
      state.lastApiResponse = payload;
      // Store villain call ranges if available
      if (payload?.villain_call_ranges) {
        state.villainCallRanges = payload.villain_call_ranges;
        console.log('Stored villain call ranges:', Object.keys(payload.villain_call_ranges));
      }
      // Store villain future call range if available
      if (payload?.villain_future_call_range) {
        state.villainFutureCallRange = payload.villain_future_call_range;
        console.log('Stored villain future call range:', Object.keys(payload.villain_future_call_range).length, 'hands');
      }
    },
    setLastAggressor: (state, { payload }) => {
      // payload: 'hero' | 'villain' | null
      state.lastAggressor = payload;
      console.log(`[Redux:setLastAggressor] lastAggressor set to: ${payload}`);
    },
    setVillainPosition: (state, { payload }) => {
      const position = payload;
      state.selectedVillainPosition = position;
      console.log(`Villain position set to: ${position}`);
    },
    setVillainCallRange: (state) => {
      // Set villain range when villain calls
      console.log(`\n=== VILLAIN CALL BUTTON PRESSED ===`);
      console.log(`Current Street: ${state.currentStreet}`);
      console.log(`Selected Villain Position: ${state.selectedVillainPosition}`);
      console.log(`Available Villain Call Ranges:`, Object.keys(state.villainCallRanges));
      console.log(`API Response Villain Range Count:`, state.lastApiResponse?.villain_range ? Object.keys(state.lastApiResponse.villain_range).length : 0);
      console.log(`Future Call Range Count:`, Object.keys(state.villainFutureCallRange).length);
      
      // For preflop, prioritize position-based ranges over API response
      if (state.currentStreet === 'preflop' && state.selectedVillainPosition && state.villainCallRanges[state.selectedVillainPosition]) {
        // Use position-based range for preflop
        state.selectedVillainRange = state.villainCallRanges[state.selectedVillainPosition];
        console.log(`Using position-based villain call range (preflop)`);
        console.log(`Villain Position: ${state.selectedVillainPosition}`);
        console.log(`Villain Range Count: ${Object.keys(state.selectedVillainRange).length} hands`);
      } else if (state.lastApiResponse?.villain_range && Object.keys(state.lastApiResponse.villain_range).length > 0) {
        // Use villain range from latest API response (postflop villain decision)
        state.selectedVillainRange = state.lastApiResponse.villain_range;
        console.log(`Using villain range from API response`);
        console.log(`Villain Range Count: ${Object.keys(state.selectedVillainRange).length} hands`);
      } else if (state.villainFutureCallRange && Object.keys(state.villainFutureCallRange).length > 0) {
        // Use pre-calculated future call range (postflop)
        state.selectedVillainRange = state.villainFutureCallRange;
        console.log(`Using pre-calculated villain future call range`);
        console.log(`Villain Range Count: ${Object.keys(state.selectedVillainRange).length} hands`);
      } else if (state.selectedVillainPosition && state.villainCallRanges[state.selectedVillainPosition]) {
        // Fall back to position-based range (preflop)
        state.selectedVillainRange = state.villainCallRanges[state.selectedVillainPosition];
        console.log(`Using position-based villain call range (preflop)`);
        console.log(`Villain Position: ${state.selectedVillainPosition}`);
        console.log(`Villain Range Count: ${Object.keys(state.selectedVillainRange).length} hands`);
      } else {
        console.log('No villain range available for calling');
        console.log(`API response villain range: ${Object.keys(state.lastApiResponse?.villain_range || {}).length} hands`);
        console.log(`Future call range: ${Object.keys(state.villainFutureCallRange).length} hands`);
        console.log(`Selected Position: ${state.selectedVillainPosition}`);
        console.log(`Available Positions: ${Object.keys(state.villainCallRanges).join(', ')}`);
        return;
      }
      
      // Show first 10 hands of villain range
      if (state.selectedVillainRange && Object.keys(state.selectedVillainRange).length > 0) {
        console.log(`\nFirst 10 hands in villain calling range:`);
        const hands = Object.entries(state.selectedVillainRange);
        for (let i = 0; i < Math.min(10, hands.length); i++) {
          const [hand, percentage] = hands[i];
          console.log(`  ${hand}: ${percentage}%`);
        }
        if (hands.length > 10) {
          console.log(`  ... and ${hands.length - 10} more hands`);
        }
      } else {
        console.log(`No hands in villain calling range`);
      }
    },
    setRandomizerEnabled: (state, { payload }) => {
      state.randomizerEnabled = !!payload;
      console.log(`[Redux:settings] randomizerEnabled -> ${state.randomizerEnabled}`);
    },
    toggleRandomizerEnabled: (state) => {
      state.randomizerEnabled = !state.randomizerEnabled;
      console.log(`[Redux:settings] randomizerEnabled toggled -> ${state.randomizerEnabled}`);
    },
    clearVillainRanges: (state) => {
      state.villainCallRanges = {};
      state.villainFutureCallRange = {};
      state.selectedVillainRange = {};
      state.selectedVillainPosition = null;
    },
    addBet: (state, { payload }) => {
      // payload: { street, actor, action, betSizeBB?, heroPos?, villainPos?, betValue? }
      if (!payload?.street || !payload?.actor || !payload?.action) return;
      
      console.log(`%c========== REDUX: addBet START ==========`, 'background: #00f; color: #fff; font-weight: bold; padding: 4px;');
      console.log(`%c[Redux:addBet] Street: ${payload.street} | Actor: ${payload.actor} | Action: ${payload.action}`, 'background: #333; color: #fff; padding: 2px;');
      console.log(`%c[Redux:addBet] BEFORE - lastAggressor: ${state.lastAggressor}`, 'background: #f90; color: #000; font-weight: bold; padding: 2px;');
      console.log(`[Redux:addBet] Current street: ${state.currentStreet}`);
      
      const entry = {
        street: payload.street,
        actor: payload.actor,
        action: payload.action,
      };
      
      // Calculate bet ratio as bet_size / pot_size (where pot size doesn't include the current bet)
      let betRatio = null;
      if (payload.betSizeBB != null && payload.betSizeBB > 0) {
        // For bets/raises: bet_ratio = bet_size / current_pot_size
        // For calls: bet_ratio = call_amount / current_pot_size
        if (payload.action === 'bet' || payload.action === 'open' || payload.action === '3bet' || payload.action === '4bet') {
          betRatio = payload.betSizeBB / state.potSize;
        } else if (payload.action === 'call') {
          const positionKey = payload.actor === 'hero' ? payload.heroPos : payload.villainPos;
          const alreadyInvested = state.investedDic[positionKey] || 0;
          const callAmount = payload.betSizeBB - alreadyInvested;
          if (callAmount > 0) {
            betRatio = callAmount / state.potSize;
          }
        }
        state.betRatio = betRatio;
        console.log(`[Redux:addBet] Bet ratio calculated: ${betRatio} (${payload.betSizeBB}BB / ${state.potSize}BB pot)`);
      }
      
      if (payload.betSizeBB != null) {
        entry.betSizeBB = Number(payload.betSizeBB);
        state.lastBetSizeBB = entry.betSizeBB;
        console.log('[Redux:addBet] Stored bet with size:', entry);
        
        // Update pot size and investments immediately
        const betSizeBB = Number(payload.betSizeBB);
        const actor = payload.actor;
        const action = payload.action;
        const heroPos = payload.heroPos;
        const villainPos = payload.villainPos;
        let actionAmountAdded = null; // amount added to the pot for this specific action
        
        console.log(`[Redux:addBet] Updating pot size and investments for ${actor} ${action} ${betSizeBB}BB`);
        console.log(`[Redux:addBet] Hero pos: ${heroPos}, Villain pos: ${villainPos}`);
        
        if (action === 'bet' || action === 'open' || action === '3bet' || action === '4bet') {
          // Track last aggressor on any bet/raise
          console.log(`%c[Redux:addBet] BET/RAISE DETECTED`, 'background: #f0f; color: #fff; font-weight: bold; padding: 4px;');
          console.log(`[Redux:addBet] Previous lastAggressor: ${state.lastAggressor}`);
          state.lastAggressor = actor;
          console.log(`%c[Redux:addBet] ✓ New lastAggressor set to: ${actor}`, 'background: #0f0; color: #000; font-weight: bold; padding: 2px;');
          // This is a bet/raise - add only the additional amount beyond prior investment
          const positionKey = actor === 'hero' ? heroPos : villainPos;
          if (positionKey && state.investedDic[positionKey] !== undefined) {
            const previousInvestment = state.investedDic[positionKey] || 0;
            
            // Enforce 100BB maximum investment limit
            const maxTotalInvestment = 100;
            const cappedBetSizeBB = Math.min(betSizeBB, maxTotalInvestment);
            
            if (cappedBetSizeBB !== betSizeBB) {
              console.log(`[Redux:addBet] Bet size capped at 100BB limit: ${betSizeBB}BB -> ${cappedBetSizeBB}BB`);
            }
            
            const investmentIncrease = cappedBetSizeBB - previousInvestment;
            if (investmentIncrease > 0) {
              state.potSize += investmentIncrease;
              actionAmountAdded = investmentIncrease;
            }
            state.investedDic[positionKey] = cappedBetSizeBB;
            
            // Also update all-streets cumulative total
            if (state.investedDicAllStreets[positionKey] !== undefined) {
              state.investedDicAllStreets[positionKey] += investmentIncrease;
              console.log(`[Redux:addBet] ${positionKey} all-streets total updated by ${investmentIncrease}BB (now ${state.investedDicAllStreets[positionKey]}BB)`);
            }
            
            // Update stack size by reducing it by the investment increase
            if (state.stackSizesDic[positionKey] !== undefined) {
              state.stackSizesDic[positionKey] -= investmentIncrease;
              console.log(`[Redux:addBet] ${positionKey} stack reduced by ${investmentIncrease}BB (now ${state.stackSizesDic[positionKey]}BB)`);
            }
            
            console.log(`[Redux:addBet] Added ${investmentIncrease}BB to pot from ${actor} (${positionKey}) ${action} (to ${cappedBetSizeBB}BB total invested)`);
            console.log(`[Redux:addBet] ${positionKey} invested: ${state.investedDic[positionKey]}BB`);
            console.log(`[Redux:addBet] New pot size: ${state.potSize}BB`);
          }
        } else if (action === 'call') {
          // This is a call - add only the difference between current bet and already invested
          const positionKey = actor === 'hero' ? heroPos : villainPos;
          if (positionKey && state.investedDic[positionKey] !== undefined) {
            // If no betSizeBB provided in payload, fall back to the last bet size stored in slice
            const currentBetSize = (payload.betSizeBB != null ? Number(payload.betSizeBB) : (state.lastBetSizeBB != null ? Number(state.lastBetSizeBB) : 0));
            const alreadyInvested = state.investedDic[positionKey] || 0;
            
            // Enforce 100BB maximum investment limit for calls
            const maxTotalInvestment = 100;
            const cappedCallSize = Math.min(currentBetSize, maxTotalInvestment);
            const callAmount = cappedCallSize - alreadyInvested;
            
            if (cappedCallSize !== currentBetSize) {
              console.log(`[Redux:addBet] Call size capped at 100BB limit: ${currentBetSize}BB -> ${cappedCallSize}BB`);
            }
            
            if (callAmount > 0) {
              state.potSize += callAmount;
              state.investedDic[positionKey] = alreadyInvested + callAmount; // increment only by the diff
              actionAmountAdded = callAmount;
              
              // Also update all-streets cumulative total
              if (state.investedDicAllStreets[positionKey] !== undefined) {
                state.investedDicAllStreets[positionKey] += callAmount;
                console.log(`[Redux:addBet] ${positionKey} all-streets total updated by ${callAmount}BB (now ${state.investedDicAllStreets[positionKey]}BB)`);
              }
              
              // Update stack size by reducing it by the call amount
              if (state.stackSizesDic[positionKey] !== undefined) {
                state.stackSizesDic[positionKey] -= callAmount;
                console.log(`[Redux:addBet] ${positionKey} stack reduced by ${callAmount}BB (now ${state.stackSizesDic[positionKey]}BB)`);
              }
              
              console.log(`[Redux:addBet] ${actor} (${positionKey}) called ${callAmount}BB (was at ${alreadyInvested}BB, now at ${state.investedDic[positionKey]}BB)`);
              console.log(`[Redux:addBet] Added ${callAmount}BB to pot from ${actor} call`);
              console.log(`[Redux:addBet] New pot size: ${state.potSize}BB`);
            } else {
              console.log(`[Redux:addBet] ${actor} (${positionKey}) call - no additional money added (already at ${alreadyInvested}BB)`);
            }
          }
          
          // Calculate pot_type when someone calls preflop (action is complete)
          if (payload.street === 'preflop') {
            // Count number of 'bet' actions in the current bets array (including this call)
            const preflopBets = state.bets.filter(b => b.street === 'preflop' && b.action === 'bet');
            const betCount = preflopBets.length;
            
            if (betCount === 0) {
              state.potType = 'limp';
            } else if (betCount === 1) {
              state.potType = 'open';
            } else if (betCount === 2) {
              state.potType = '3bet';
            } else if (betCount >= 3) {
              state.potType = '4bet';
            }
            
            console.log(`%c[Redux:addBet] CALL on preflop - pot_type calculated:`, 'background: #0f0; color: #000; font-weight: bold; padding: 2px;');
            console.log(`[Redux:addBet] Preflop bet count: ${betCount}`);
            console.log(`[Redux:addBet] pot_type set to: ${state.potType}`);
          }
        }
        
        // Update hero invested amount
        if (heroPos && state.investedDic[heroPos] !== undefined) {
          state.heroInvested = state.investedDic[heroPos];
          console.log(`[Redux:addBet] Hero invested updated to: ${state.heroInvested}BB`);
          
          // Check if hero invested more than 9BB preflop
          if (state.currentStreet === 'preflop' && state.heroInvested > 9 && !state.investedTenPercentPre) {
            state.investedTenPercentPre = true;
            console.log(`[Redux:addBet] Hero invested more than 9BB preflop - investedTenPercentPre set to TRUE`);
          }
        }

        // Store the per-action amount (for UI display)
        if (actionAmountAdded != null && actionAmountAdded > 0) {
          entry.amountBB = actionAmountAdded;
        }
      }
      
      // Track last actions
      if (payload.actor === 'hero') {
        state.heroAction = payload.action;
        console.log(`[Redux:addBet] Hero action: ${payload.action}`);
      } else if (payload.actor === 'villain') {
        state.villainAction = payload.action;
        console.log(`[Redux:addBet] Villain action: ${payload.action}`);
      }
      
      // If the current lastAggressor checks, set lastAggressor to null
      if (payload.action === 'check') {
        console.log(`%c[Redux:addBet] CHECK DETECTED by ${payload.actor}`, 'background: #ff0; color: #000; font-weight: bold; padding: 4px; font-size: 14px;');
        console.log(`%c[Redux:addBet] Current lastAggressor before check: ${state.lastAggressor}`, 'background: #f90; color: #000; padding: 2px;');
        if (state.lastAggressor === payload.actor) {
          console.log(`%c[Redux:addBet] ✓✓✓ MATCH! Current aggressor (${payload.actor}) checked - RESETTING lastAggressor to NULL`, 'background: #0f0; color: #000; font-weight: bold; padding: 4px;');
          state.lastAggressor = null;
        } else {
          console.log(`%c[Redux:addBet] ✗ NO MATCH: ${payload.actor} checked but is NOT the current aggressor (${state.lastAggressor})`, 'background: #f00; color: #fff; padding: 2px;');
        }
      }
      
      // Update betting list with action string
      state.betting.push(payload.action);
      console.log(`[Redux:addBet] Added '${payload.action}' to betting list: [${state.betting.join(', ')}]`);
      
      // Derive last action and last two actions from betting list
      state.lastAction = state.betting[state.betting.length - 1] || null;
      state.lastTwoActions = state.betting.slice(-2);
      
      console.log(`[Redux:addBet] Last action: ${state.lastAction}`);
      console.log(`[Redux:addBet] Last two actions: [${state.lastTwoActions.join(', ')}]`);
      
      // Check if street has ended and clear betting list for next street
      const bettingLength = state.betting.length;
      const lastTwoActions = state.lastTwoActions;
      
      console.log(`[Redux:addBet] Checking street end: lastTwoActions = [${lastTwoActions.join(', ')}]`);
      
      // Street ends when:
      // 1. Both players check (check, check)
      // 2. There's a call after a bet (bet, call)
      // 3. There's a fold after a bet (bet, fold)
      const streetEnded = (
        (lastTwoActions.length === 2 && lastTwoActions[0] === 'check' && lastTwoActions[1] === 'check') ||
        (lastTwoActions.length === 2 && lastTwoActions[0] === 'bet' && lastTwoActions[1] === 'call') ||
        (lastTwoActions.length === 2 && lastTwoActions[0] === 'bet' && lastTwoActions[1] === 'fold')
      );
      
      console.log(`[Redux:addBet] Street ended? ${streetEnded}`);
      
      if (streetEnded) {
        console.log(`%c[Redux:addBet] STREET ENDED with actions: [${lastTwoActions.join(', ')}]`, 'background: #f0f; color: #fff; font-weight: bold; padding: 4px;');
        
        // If both players checked (check-check), reset lastAggressor to null
        if (lastTwoActions.length === 2 && lastTwoActions[0] === 'check' && lastTwoActions[1] === 'check') {
          console.log(`%c[Redux:addBet] CHECK-CHECK DETECTED - Resetting lastAggressor from ${state.lastAggressor} to null`, 'background: #0ff; color: #000; font-weight: bold; padding: 4px;');
          state.lastAggressor = null;
        }
        
        console.log(`[Redux:addBet] Clearing betting list for next street`);
        state.betting = []; // Clear betting list for next street
        state.lastAction = null;
        state.lastTwoActions = [];
      }
      
      state.bets.push(entry);
      console.log('[Redux:addBet] Current street bets:', state.bets.filter(b => b.street === payload.street));
      
      console.log(`%c[Redux:addBet] AFTER - lastAggressor: ${state.lastAggressor}`, 'background: #f90; color: #000; font-weight: bold; padding: 2px;');
      console.log(`%c========== REDUX: addBet END ==========`, 'background: #00f; color: #fff; font-weight: bold; padding: 4px;');
    },
    resetBets: (state) => {
      state.bets = [];
      state.betting = [];
    },
    resetHeroRange: (state) => {
      state.selectedHeroRange = {};
      state.selectedAction = null;
    },
    resetVillainRange: (state) => {
      state.villainCallRanges = {};
      state.villainFutureCallRange = {};
      state.selectedVillainRange = {};
      state.selectedVillainPosition = null;
    },
    resetPotAndInvestments: (state) => {
      state.potSize = 1.5; // Reset to blinds
      state.investedDic = { lo: 0, hi: 0, co: 0, btn: 0, sb: 0.5, bb: 1.0 };
      state.investedDicAllStreets = { lo: 0, hi: 0, co: 0, btn: 0, sb: 0.5, bb: 1.0 };
      state.stackSizesDic = { lo: 100, hi: 100, co: 100, btn: 100, sb: 99.5, bb: 99 }; // Reset to initial values (SB/BB with blinds posted)
      state.heroInvested = 0.0;
      state.investedTenPercentPre = false; // Reset the 10% preflop flag
      state.betRatio = null;
      state.currentStreet = 'preflop';
      state.frozenPotSize = { flop: null, turn: null, river: null };
      state.heroAction = null;
      state.villainAction = null;
      state.lastAction = null;
      state.lastTwoActions = [];
      state.betting = [];
      state.lastAggressor = null;
      state.potType = null; // Reset pot type
      console.log('[Redux:resetPotAndInvestments] Reset pot size, investments, and stack sizes to defaults');
    },
    updatePotSize: (state, { payload }) => {
      state.potSize = payload;
      console.log(`[Redux:updatePotSize] Pot size updated to: ${payload}BB`);
    },
    updateInvestedDic: (state, { payload }) => {
      state.investedDic = { ...state.investedDic, ...payload };
      console.log(`[Redux:updateInvestedDic] Invested dic updated:`, payload);
    },
    updateHeroInvested: (state, { payload }) => {
      state.heroInvested = payload;
      console.log(`[Redux:updateHeroInvested] Hero invested updated to: ${payload}BB`);
    },
    transitionToNewStreet: (state, { payload }) => {
      // payload: { newStreet }
      const newStreet = payload.newStreet;
      const oldStreet = state.currentStreet;
      
      console.log(`[Redux:transitionToNewStreet] Transitioning from ${oldStreet} to ${newStreet}`);
      
      // Capture frozen pot size at the start of each postflop street (flop/turn/river)
      if (newStreet === 'flop' || newStreet === 'turn' || newStreet === 'river') {
        if (state.frozenPotSize[newStreet] == null) {
          state.frozenPotSize[newStreet] = state.potSize;
          console.log(`[Redux:transitionToNewStreet] Frozen pot for ${newStreet} set to ${state.potSize}BB`);
        }
      }
      
      // Note: investedDicAllStreets is now updated in real-time during each bet/call
      // No need to add here as it would cause double-counting
      console.log(`[Redux:transitionToNewStreet] All-streets totals (already updated in real-time):`, state.investedDicAllStreets);
      
      // Reset current street investments (except for preflop which keeps blinds)
      if (newStreet === 'preflop') {
        state.investedDic = { lo: 0, hi: 0, co: 0, btn: 0, sb: 0.5, bb: 1.0 };
      } else {
        state.investedDic = { lo: 0, hi: 0, co: 0, btn: 0, sb: 0, bb: 0 };
      }
      
      // Update current street
      state.currentStreet = newStreet;
      // Reset per-street last actions
      state.heroAction = null;
      state.villainAction = null;
      
      // Update hero invested from all-streets total
      if (state.investedDicAllStreets[state.currentStreet]) {
        state.heroInvested = state.investedDicAllStreets[state.currentStreet];
      }
      
      console.log(`[Redux:transitionToNewStreet] Street transition complete. Current street: ${newStreet}`);
      console.log(`[Redux:transitionToNewStreet] Current street investments:`, state.investedDic);
      console.log(`[Redux:transitionToNewStreet] All streets total:`, state.investedDicAllStreets);
      console.log(`[Redux:transitionToNewStreet] Current stack sizes:`, state.stackSizesDic);
    },
  },
});

export const { setBetOption, setCallOption, setCheckOption, setFoldOption, setApiResponse, addBet, resetBets, resetHeroRange, setVillainPosition, setVillainCallRange, clearVillainRanges, resetVillainRange, resetPotAndInvestments, updatePotSize, updateInvestedDic, updateHeroInvested, transitionToNewStreet, setLastAggressor, setRandomizerEnabled, toggleRandomizerEnabled } = betSlice.actions;
export default betSlice.reducer;